import React, { FC, useState, useEffect, useMemo, Dispatch, SetStateAction, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import filter from 'lodash/filter';
import includes from 'lodash/includes';

import { IDynamicUiField } from 'plugins/api/types';
import { useLoadData } from 'plugins/api/endpoints';

import { SearchInput, Button, Loader, Checkbox } from 'components/controls';
import Table, { IColumn } from 'components/controls/Table';

import 'plugins/theme/components/EditPanel/EditPanel.scss';

interface IQueryParams {
  page: number;
  limit: number;
  searchKeyword: string;
}

// typ danych wejściowych
interface IProps {
  field: IDynamicUiField;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  queryParams: IQueryParams;
  setQueryParams: Dispatch<SetStateAction<IQueryParams>>;
  onClose: () => void;
  onChangeHandler: (arg: any) => void;
}

const DynamicListTable: FC<IProps> = ({
  field,
  queryParams,
  setQueryParams,
  onClose,
  onChangeHandler
}) => {
  const { t } = useTranslation();

  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  
  // Cache dla danych z różnych stron - klucz: `${page}::${searchKeyword}`
  const dataCacheRef = useRef<Map<string, any[]>>(new Map());
  const lastSearchKeywordRef = useRef<string>(queryParams.searchKeyword);

  useEffect(() => {
    if (field.dictionary.multi_choice) {
      const separator = '|';
      setCheckedItems((field.value as string)?.split(separator).filter(Boolean) || []);
    } else {
      setCheckedItems(field.value ? [field.value.toString()] : []);
    }
  }, [field.dictionary.multi_choice, field.value, field.dictionary.separator_multi_choice]);

  // Czyszczenie cache przy zmianie searchKeyword
  useEffect(() => {
    const currentSearchKeyword = queryParams.searchKeyword || '';
    const lastSearchKeyword = lastSearchKeywordRef.current || '';
    
    // Czyszczymy cache tylko jeśli searchKeyword faktycznie się zmienił (nie jest to reset)
    if (lastSearchKeyword !== currentSearchKeyword && lastSearchKeyword !== '') {
      dataCacheRef.current.clear();
    }
    lastSearchKeywordRef.current = currentSearchKeyword;
  }, [queryParams.searchKeyword]);

  const {
    data: dictionaryFromUIData,
    refetch: refetchDictionaryFromUI,
    isLoading: isDataLoading
  } = useLoadData(field.dictionary.values_get_u_i_api || '', queryParams, {
    enabled: false,
    keepPreviousData: true
  });

  useEffect(() => {
    if (field.dictionary.values_get_u_i_api) {
      refetchDictionaryFromUI();
    }
  }, [field.dictionary.values_get_u_i_api, queryParams]);

  // Aktualizacja cache gdy pobieramy nowe dane
  useEffect(() => {
    if (dictionaryFromUIData?.items && Array.isArray(dictionaryFromUIData.items)) {
      const cacheKey = `${queryParams.page}::${queryParams.searchKeyword || ''}`;
      // Zapisujemy dane do cache - zawsze aktualizujemy, żeby mieć najnowsze dane
      dataCacheRef.current.set(cacheKey, [...dictionaryFromUIData.items]);
    }
  }, [dictionaryFromUIData?.items, queryParams.page, queryParams.searchKeyword]);

  // Funkcja do pobierania wszystkich danych z cache dla aktualnego searchKeyword
  const getAllCachedItems = () => {
    const allItems: any[] = [];
    const seenIds = new Set<string | number>();
    const currentSearchKeyword = queryParams.searchKeyword || '';
    
    // Iterujemy przez wszystkie wpisy w cache dla aktualnego searchKeyword
    dataCacheRef.current.forEach((items, key) => {
      const parts = key.split('::');
      if (parts.length === 2) {
        const [, searchKeyword] = parts;
        if (searchKeyword === currentSearchKeyword) {
          items.forEach((item: any) => {
            const itemId = item.id || item.value;
            if (!seenIds.has(itemId)) {
              seenIds.add(itemId);
              allItems.push(item);
            }
          });
        }
      }
    });
    
    return allItems;
  };

  const setMultiChoiceValues = () => {
    // Używamy wszystkich danych z cache, nie tylko z aktualnej strony
    const allCachedItems = getAllCachedItems();
    const results = filter(allCachedItems, (item) =>
      includes(checkedItems, item.value.toString())
    );

    onChangeHandler(results);
  };

  const columns: IColumn<any>[] = useMemo(() => {
    if (field.dictionary.multi_choice) {
      return [
        {
          title: <Trans>Wybierz</Trans>,
          align: 'left',
          renderCell: (item) => {
            return (
              <Checkbox
                onClick={() => {
                  setCheckedItems((prev) => {
                    const val = item.value.toString();
                    return prev?.includes(val)
                      ? prev.filter((v) => v !== val)
                      : [...(prev || []), val];
                  });
                }}
                checked={checkedItems?.includes(item.value.toString())}
              />
            );
          }
        },
        {
          title: <Trans>Nazwa</Trans>,
          align: 'left',
          renderCell: (item) => <span>{item.text}</span>
        }
      ];
    }

    return [
      {
        title: <Trans>Nazwa</Trans>,
        align: 'left',
        renderCell: (item) => (
          <button onClick={() => onChangeHandler(item)}>{item.text}</button>
        )
      }
    ];
  }, [field.dictionary.multi_choice, checkedItems]);

  return (
    <div>
      <div className="dynamicListSearchWrapper">
        <SearchInput
          placeholder={`${t('Szukaj')}...`}
          value={queryParams.searchKeyword}
          onChange={(value) => setQueryParams(() => ({ searchKeyword: value, page: 1, limit: 10 }))}
        />
        {!!queryParams.searchKeyword && (
          <button
            className="clearFilters"
            color="secondary"
            onClick={() => setQueryParams(() => ({ searchKeyword: '', page: 1, limit: 10 }))}>
            <Trans>Wyczyść filtry</Trans>
          </button>
        )}
      </div>
      <div className="dynamicListTableWrapper">
        {isDataLoading ? (
          <Loader />
        ) : (
          <Table
            columns={columns}
            dataSource={dictionaryFromUIData?.items || []}
            rowKey="id"
            pagination={{
              page: queryParams.page || 1,
              pagesCount: dictionaryFromUIData?.total_pages || 1,
              onChange: (page) => setQueryParams((prevState) => ({ ...prevState, page }))
            }}
          />
        )}
      </div>

      <div className="confirmationModalActions">
        <Button color="secondary" ghost onClick={() => onClose()}>
          <Trans>Anuluj</Trans>
        </Button>

        {field.dictionary.multi_choice && (
          <Button color="secondary" ghost onClick={() => setMultiChoiceValues()}>
            <Trans>Wybierz</Trans>
          </Button>
        )}
      </div>
    </div>
  );
};

export default DynamicListTable;
